import { useCountry } from 'hooks'
import { RequestOptions } from 'mixpanel-browser'
import { useCallback } from 'react'
import { config } from '../../config'
import { EventType } from '../../constants'
import { logRocket } from '../../startup'
import type { AnyEventData, EventData } from '../../types'
import { logFactory } from '../../util'
import { getCommonEventData } from '../../util/analytics/getEventData'
import { useMixpanel } from '../useMixpanel'
import { useSearchParameters } from '../useSearchParameters'

type EventTrackingFunction = (type: EventType, options?: Options) => Promise<void>

interface Options {
  data?: AnyEventData
  options?: RequestOptions
}

interface EventTrackingContext {
  track: EventTrackingFunction
}

const log = logFactory('hooks', 'useEventTracking')

const parseEventData = (data?: AnyEventData) => {
  const commonData = getCommonEventData()
  return {
    ...data,
    ...commonData,
    environment: config.APP_ENV,
  }
}

const noop = (type: EventType, data?: AnyEventData) => {
  const event = parseEventData(data)
  log(`❌ Mixpanel not ready. Not tracking "${type}" event`, event)
}

const useEventTracking = (): EventTrackingContext => {
  const searchParams = useSearchParameters()
  const country = useCountry()
  const { mixpanel } = useMixpanel()

  const { appointment_type, customer_gid, journey, opportunity_gid, shop_gid } = searchParams

  const defaultData: EventData = {
    country: country as string,
    customerGid: customer_gid as string,
    isInitialValid: true,
    journey: journey as string,
    opportunityGid: opportunity_gid as string,
    shopGid: shop_gid as string,
    type: appointment_type as string,
  }

  const track = useCallback(
    async (type: EventType, { data, options }: Options = {}) => {
      const actualData: EventData = {
        ...defaultData,
        ...data,
      }

      const event = parseEventData(actualData)

      log(`🐾 Tracking "${type}" event`, event)

      // When running locally, this may fail, preventing mix panel events from firing.
      const logRocketSessionUrl = await logRocket.getSessionURL()

      const fullEvent = {
        ...event,
        logRocketSessionUrl,
      }

      // Track to LogRocket (always)
      logRocket?.track(type, fullEvent)

      // Track to Mixpanel (only if available)
      if (!mixpanel) return noop(type, actualData)
      return mixpanel.track(type, fullEvent, options)
    },
    [defaultData, mixpanel]
  )

  return { track }
}

export type { EventTrackingFunction }
export { useEventTracking }
